import { useSearchParams } from "react-router-dom";
import { InlineNativeSelect } from "@/components/v2/InlineNativeSelect";
import { LeaderboardTable } from "@/components/v2/LeaderboardTable";
import { SectionBlock } from "@/components/v2/SectionBlock";
import { useMarketLeaderboard } from "@/hooks/useHip3Analytics";
import { leaderboardValueHeader, normalizeLeaderboardRows } from "@/lib/analytics-transforms";
import type { AnalyticsWindow } from "@/api/hip3";

function asAnalyticsWindow(raw: string | null): AnalyticsWindow {
  if (raw === "24h" || raw === "7d" || raw === "30d") return raw;
  return "24h";
}

function rowsFrom(data: ReturnType<typeof useMarketLeaderboard>["data"]) {
  if (!data) return [];
  return normalizeLeaderboardRows(data);
}

export function LeaderboardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const window = asAnalyticsWindow(searchParams.get("window"));

  const oiGrowth = useMarketLeaderboard({ metric: "oiGrowth", window, limit: 10 });
  const volumeGrowth = useMarketLeaderboard({ metric: "volumeGrowth", window, limit: 10 });
  const tightest = useMarketLeaderboard({ metric: "spreadTightest", window, limit: 10 });
  const widest = useMarketLeaderboard({ metric: "spreadWidest", window, limit: 10 });
  const fundingHighest = useMarketLeaderboard({ metric: "fundingHighest", window, limit: 10 });
  const fundingVolatility = useMarketLeaderboard({ metric: "fundingVolatility", window, limit: 10 });
  const isWindowUpdating =
    [oiGrowth, volumeGrowth, tightest, widest, fundingHighest, fundingVolatility].some(
      (query) => query.isFetching
    ) &&
    [oiGrowth, volumeGrowth, tightest, widest, fundingHighest, fundingVolatility].some(
      (query) => Boolean(query.data)
    );

  return (
    <div className="space-y-6">
      <SectionBlock
        title="Leaderboards"
        subtitle="Discover the most notable HIP-3 markets by growth, spread quality, and funding signals."
        actions={
          <div className="flex items-center gap-2">
            {isWindowUpdating ? (
              <span className="text-[0.65rem] font-mono uppercase tracking-wide text-primary">
                Updating...
              </span>
            ) : null}
            <InlineNativeSelect
              value={window}
              onChange={(event) => setSearchParams({ window: event.target.value }, { replace: true })}
              className="h-8 border border-border/70 bg-card pl-2 text-xs font-mono"
            >
              <option value="24h">24h window</option>
              <option value="7d">7d window</option>
              <option value="30d">30d window</option>
            </InlineNativeSelect>
          </div>
        }
      >
        <div className="grid gap-3 lg:grid-cols-2">
          <LeaderboardTable
            title={`Top OI Growth (${window})`}
            rows={rowsFrom(oiGrowth.data)}
            valueHeader={leaderboardValueHeader("oiGrowth")}
            isLoading={oiGrowth.isLoading}
            isUpdating={oiGrowth.isFetching && Boolean(oiGrowth.data)}
          />
          <LeaderboardTable
            title={`Top Volume Growth (${window})`}
            rows={rowsFrom(volumeGrowth.data)}
            valueHeader={leaderboardValueHeader("volumeGrowth")}
            isLoading={volumeGrowth.isLoading}
            isUpdating={volumeGrowth.isFetching && Boolean(volumeGrowth.data)}
          />
        </div>
      </SectionBlock>

      <SectionBlock title="Spread Quality" subtitle="Both the tightest and widest execution conditions.">
        <div className="grid gap-3 lg:grid-cols-2">
          <LeaderboardTable
            title={`Tightest Spreads (${window})`}
            rows={rowsFrom(tightest.data)}
            valueHeader={leaderboardValueHeader("spreadTightest")}
            isLoading={tightest.isLoading}
            isUpdating={tightest.isFetching && Boolean(tightest.data)}
          />
          <LeaderboardTable
            title={`Widest Spreads (${window})`}
            rows={rowsFrom(widest.data)}
            valueHeader={leaderboardValueHeader("spreadWidest")}
            isLoading={widest.isLoading}
            isUpdating={widest.isFetching && Boolean(widest.data)}
          />
        </div>
      </SectionBlock>

      <SectionBlock title="Funding Signals" subtitle="Funding level and instability regimes across markets.">
        <div className="grid gap-3 lg:grid-cols-2">
          <LeaderboardTable
            title={`Highest Funding (${window})`}
            rows={rowsFrom(fundingHighest.data)}
            valueHeader={leaderboardValueHeader("fundingHighest")}
            isLoading={fundingHighest.isLoading}
            isUpdating={fundingHighest.isFetching && Boolean(fundingHighest.data)}
          />
          <LeaderboardTable
            title={`Funding Instability (${window})`}
            rows={rowsFrom(fundingVolatility.data)}
            valueHeader={leaderboardValueHeader("fundingVolatility")}
            subtitle="Instability = funding standard deviation in bps over the selected window."
            isLoading={fundingVolatility.isLoading}
            isUpdating={fundingVolatility.isFetching && Boolean(fundingVolatility.data)}
          />
        </div>
      </SectionBlock>
    </div>
  );
}
